import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, Bell, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function EmailSubscribeSection({ variant = 'default', className = '' }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/api/subscribers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, subscription_type: 'general' })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to subscribe');
      }

      setSubscribed(true);
      toast.success('You\'re subscribed! Check your email for confirmation.');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (subscribed) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`flex items-center justify-center gap-2 p-4 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 ${className}`}
      >
        <CheckCircle className="w-5 h-5 text-green-500" />
        <span className="text-green-700 dark:text-green-300 font-medium">Thanks for subscribing!</span>
      </motion.div>
    );
  }

  // Banner variant - full width for homepage
  if (variant === 'banner') {
    return (
      <div className={`bg-gradient-to-r from-navy-blue-900 to-navy-blue-800 text-white py-12 px-4 ${className}`}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-white/10 rounded-full">
              <Bell className="w-8 h-8" />
            </div>
          </div>
          <h3 className="text-2xl font-bold mb-2">Stay Informed</h3>
          <p className="text-white/80 mb-6 max-w-lg mx-auto">
            Get notified when new cases are added or major matches are found. We only send important updates.
          </p>
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 h-12"
                required
                data-testid="subscribe-email-banner"
              />
            </div>
            <Button
              type="submit"
              className="h-12 px-8 bg-safety-orange-500 hover:bg-safety-orange-600 text-white"
              disabled={loading}
              data-testid="subscribe-btn-banner"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Subscribe'}
            </Button>
          </form>
          <p className="text-xs text-white/50 mt-4">
            No spam. Unsubscribe anytime. Read our{' '}
            <a href="/privacy-policy" className="underline hover:text-white/70">Privacy Policy</a>
          </p>
        </div>
      </div>
    );
  }

  // Compact variant - for sidebar or footer
  if (variant === 'compact') {
    return (
      <div className={`p-4 rounded-lg border bg-card ${className}`}>
        <div className="flex items-center gap-2 mb-2">
          <Bell className="w-4 h-4 text-safety-orange-500" />
          <span className="font-medium text-sm">Get Updates</span>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Notified when new cases are added or matches found.
        </p>
        <form onSubmit={handleSubscribe} className="space-y-2">
          <Input
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-9 text-sm"
            required
            data-testid="subscribe-email-compact"
          />
          <Button
            type="submit"
            size="sm"
            className="w-full bg-safety-orange-500 hover:bg-safety-orange-600"
            disabled={loading}
            data-testid="subscribe-btn-compact"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe'}
          </Button>
        </form>
      </div>
    );
  }

  // Default variant - card style
  return (
    <div className={`p-6 rounded-xl border-2 border-dashed bg-muted/30 ${className}`}>
      <div className="text-center mb-4">
        <div className="inline-flex p-3 bg-safety-orange-100 dark:bg-safety-orange-900/30 rounded-full mb-3">
          <Bell className="w-6 h-6 text-safety-orange-500" />
        </div>
        <h3 className="font-semibold text-lg">Get Notified</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Be the first to know when new cases are added or major matches are found.
        </p>
      </div>
      <form onSubmit={handleSubscribe} className="space-y-3">
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pl-10"
            required
            data-testid="subscribe-email-default"
          />
        </div>
        <Button
          type="submit"
          className="w-full bg-safety-orange-500 hover:bg-safety-orange-600"
          disabled={loading}
          data-testid="subscribe-btn-default"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe for Updates'}
        </Button>
      </form>
      <p className="text-xs text-center text-muted-foreground mt-3">
        We only email important updates. No spam.
      </p>
    </div>
  );
}
